/**
 * Enum values + UI-semantics maps for the workflow execution-tracking console.
 * Every string set mirrors the `workflow-webapp` contract exactly (see the API
 * reference). Each enum is a `readonly` tuple (for filter iteration) paired with
 * a derived union type (for prop/DTO typing).
 */

/** Run-level status (`summary.status`) — reconciled with Temporal. */
export const RUN_STATUSES = [
  'PENDING',
  'RUNNING',
  'AWAITING_GATE',
  'COMPLETED',
  'FAILED',
  'TERMINATED',
  'RESET',
  'SKIPPED',
] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];

/** Stage-level status. `RECONCILE_FAILED` surfaces the reconciliation mismatch. */
export const STAGE_STATUSES = [
  'PENDING',
  'RUNNING',
  'AWAITING_GATE',
  'COMPLETED',
  'FAILED',
  'SKIPPED',
  'RECONCILE_FAILED',
] as const;
export type StageStatus = (typeof STAGE_STATUSES)[number];

/** Per-product item status. */
export const ITEM_STATUSES = ['PENDING', 'SUCCESS', 'FAILED', 'SKIPPED'] as const;
export type ItemStatus = (typeof ITEM_STATUSES)[number];

/** Execution-node level in the run hierarchy. */
export const LEVELS = ['FLOW', 'COORDINATOR', 'BATCH', 'SUB_BATCH'] as const;
export type Level = (typeof LEVELS)[number];

/** Pipeline stage (19). */
export const STAGES = [
  'IMPORT',
  'IMAGE_GATE',
  'CURATION',
  'FETCH',
  'PRE_ENRICH_SCORE',
  'SPLIT',
  'SAMPLE_SELECT',
  'SAMPLE',
  'ARM_ASSIGNMENT',
  'ENRICHMENT',
  'POST_ENRICH_SCORE',
  'ROLLUPS',
  'ENRICHMENT_INSIGHTS',
  'APPROVAL',
  'DELIVERY',
  'EXPORT_PLAN',
  'EXPORT_WRITE',
  'EXPORT_COMPOSE',
  'TERMINAL',
] as const;
export type Stage = (typeof STAGES)[number];

/** Flow type (15) — the workflow kind a run belongs to. */
export const FLOW_TYPES = [
  'CATALOG_OPERATIONS',
  'ENRICHMENT',
  'SCHEDULED_ENRICHMENT',
  'SCHEDULED_PIPELINE',
  'SCHEDULED_SCORING',
  'SCHEDULED_SAMPLE_ENRICHMENT',
  'EXPERIMENT_PIPELINE',
  'EXPERIMENT_SCORING',
  'EXPERIMENT_SAMPLE_ENRICHMENT',
  'EXPERIMENT_SPLIT',
  'EXPERIMENT_ARM_ASSIGNMENT',
  'TRIAL_SCORING',
  'TRIAL_SAMPLE_ENRICHMENT',
  'EXPORT',
  'FEED_AUDIT',
] as const;
export type FlowType = (typeof FLOW_TYPES)[number];

/** Control-action kind recorded in the audit timeline. */
export const CONTROL_ACTIONS = [
  'RUN',
  'PAUSE',
  'RESUME',
  'RETRY_STAGE',
  'RETRY_FAILED_ITEMS',
  'RETRY_FROM_STEP',
  'RESTART_FROM_STAGE',
  'RETRIGGER',
  'SKIP',
  'RESET',
  'TERMINATE',
  'GATE_APPROVE',
  'GATE_REJECT',
  'GATE_JUMP_BACK',
] as const;
export type ControlAction = (typeof CONTROL_ACTIONS)[number];

/** Lineage asset (node) type. */
export const ASSET_TYPES = [
  'SYNC',
  'IMPORT_FILE',
  'CURATION_RUN',
  'SCORE_SET',
  'SAMPLE_SET',
  'ENRICHMENT_SET',
  'AGENT_BATCH',
  'ROLLUP',
  'DELIVERY_PAYLOAD',
  'EXPORT_FILE',
  'EXPORT_CHUNK',
  'EXTERNAL_DESTINATION',
] as const;
export type AssetType = (typeof ASSET_TYPES)[number];

/** Lineage link (edge) type. */
export const LINK_TYPES = ['CONSUMES', 'PRODUCES', 'DERIVES_FROM', 'HANDS_OFF'] as const;
export type LinkType = (typeof LINK_TYPES)[number];

/** Gate-outcome recorded on a decided stage (`null` until decided). */
export type GateOutcome = 'GATE_APPROVE' | 'GATE_REJECT' | 'GATE_JUMP_BACK';

/** Reconciliation verdict on a stage. `PASS`/`MATCH` are clean; the rest warn. */
export type ReconciliationResult = 'PASS' | 'MATCH' | 'FAIL' | 'MISMATCH';

/** Operator gate decision (request). */
export const GATE_DECISIONS = ['APPROVE', 'REJECT', 'JUMP_BACK'] as const;
export type GateDecision = (typeof GATE_DECISIONS)[number];

/** Retry scope (request). `BATCH` is contract-declared but returns 501 today. */
export const RETRY_SCOPES = ['STAGE', 'FAILED_ITEMS', 'BATCH'] as const;
export type RetryScope = (typeof RETRY_SCOPES)[number];

/** Live-progress data source — poll while `LIVE`, stop on `DURABLE`. */
export type ProgressSource = 'LIVE' | 'DURABLE';

/**
 * Run-health verdict (`detail.health.state`) — *why* a run looks the way it
 * does, which `RunStatus` alone can't say. `BLOCKED_ON_SIGNAL` is contract-
 * declared but never emitted today; handled defensively, never designed for.
 */
export const HEALTH_STATES = [
  'HEALTHY',
  'SLOW',
  'STALLED',
  'RETRYING',
  'BLOCKED_ON_GATE',
  'BLOCKED_ON_SIGNAL',
  'PAUSED',
  'PENDING',
  'TERMINAL',
  'UNKNOWN',
] as const;
export type HealthState = (typeof HEALTH_STATES)[number];

/** How a run was kicked off — `null` on the summary for manual triggers. */
export const TRIGGER_TYPES = ['SCHEDULED', 'ON_DEMAND'] as const;
export type ScheduleTriggerType = (typeof TRIGGER_TYPES)[number];

// ---------------------------------------------------------------------------
// UI-semantics maps
// ---------------------------------------------------------------------------

/** Semantic colour tone consumed by `WorkflowStatusChip` (design tokens). */
export type StatusTone = 'info' | 'success' | 'warning' | 'error' | 'muted';

/** Run-status → chip tone (blue running · green done · amber gate · red fail · grey rest). */
export const RUN_STATUS_TONE: Record<RunStatus, StatusTone> = {
  PENDING: 'muted',
  RUNNING: 'info',
  AWAITING_GATE: 'warning',
  COMPLETED: 'success',
  FAILED: 'error',
  TERMINATED: 'muted',
  RESET: 'muted',
  SKIPPED: 'muted',
};

/** Stage-status → chip tone. `RECONCILE_FAILED` reads as an error. */
export const STAGE_STATUS_TONE: Record<StageStatus, StatusTone> = {
  PENDING: 'muted',
  RUNNING: 'info',
  AWAITING_GATE: 'warning',
  COMPLETED: 'success',
  FAILED: 'error',
  SKIPPED: 'muted',
  RECONCILE_FAILED: 'error',
};

/** Item-status → chip tone. */
export const ITEM_STATUS_TONE: Record<ItemStatus, StatusTone> = {
  PENDING: 'muted',
  SUCCESS: 'success',
  FAILED: 'error',
  SKIPPED: 'muted',
};

/**
 * Health severity — how loudly the banner should speak. `action` means a human
 * is the blocker (gate / pause), `idle` means there is nothing to watch.
 */
export type HealthSeverity = 'ok' | 'warn' | 'critical' | 'action' | 'idle';

/** Health state → chip/banner tone. */
export const HEALTH_STATE_TONE: Record<HealthState, StatusTone> = {
  HEALTHY: 'success',
  SLOW: 'warning',
  STALLED: 'error',
  RETRYING: 'warning',
  BLOCKED_ON_GATE: 'warning',
  BLOCKED_ON_SIGNAL: 'muted',
  PAUSED: 'info',
  PENDING: 'muted',
  TERMINAL: 'muted',
  UNKNOWN: 'muted',
};

/**
 * Health state → severity. RETRYING is deliberately `warn`, never `critical`:
 * Temporal is already handling the failure, so it must not read like STALLED
 * (which is a genuinely wedged run and an escalation).
 */
export const HEALTH_STATE_SEVERITY: Record<HealthState, HealthSeverity> = {
  HEALTHY: 'ok',
  SLOW: 'warn',
  STALLED: 'critical',
  RETRYING: 'warn',
  BLOCKED_ON_GATE: 'action',
  BLOCKED_ON_SIGNAL: 'idle',
  PAUSED: 'action',
  PENDING: 'idle',
  TERMINAL: 'idle',
  UNKNOWN: 'idle',
};

/** Terminal run statuses — stop polling once a run reaches one of these. */
export const TERMINAL_RUN_STATUSES: ReadonlySet<RunStatus> = new Set([
  'COMPLETED',
  'FAILED',
  'TERMINATED',
  'SKIPPED',
]);

// ---------------------------------------------------------------------------
// Open-enum lookups
// ---------------------------------------------------------------------------

/**
 * Tone for a status value that may not be in our union. Deploys aren't atomic,
 * so the backend can emit an enum member this build has never heard of —
 * indexing a closed `Record` would hand a `undefined` style to the chip. Every
 * unknown value degrades to `muted` instead (the label still renders raw).
 */
function toneOf<T extends string>(map: Record<T, StatusTone>, value: string | null): StatusTone {
  if (!value) return 'muted';
  return (map as Record<string, StatusTone | undefined>)[value] ?? 'muted';
}

/** Run-status → tone, safe against unknown values. */
export const runStatusTone = (value: string | null): StatusTone => toneOf(RUN_STATUS_TONE, value);
/** Stage-status → tone, safe against unknown values. */
export const stageStatusTone = (value: string | null): StatusTone =>
  toneOf(STAGE_STATUS_TONE, value);
/** Item-status → tone, safe against unknown values. */
export const itemStatusTone = (value: string | null): StatusTone => toneOf(ITEM_STATUS_TONE, value);
/** Health-state → tone, safe against unknown values. */
export const healthStateTone = (value: string | null): StatusTone =>
  toneOf(HEALTH_STATE_TONE, value);

/** Health-state → severity, safe against unknown values (unknown ⇒ `idle`). */
export function healthStateSeverity(value: string | null): HealthSeverity {
  if (!value) return 'idle';
  return (HEALTH_STATE_SEVERITY as Record<string, HealthSeverity | undefined>)[value] ?? 'idle';
}
